/**
 * ErrorBoundary — Catches rendering errors in any page.
 * v4.8 Spec: "Error boundaries on every page."
 *
 * Shows a glass-card error message with retry.
 * Logs errors for debugging (future: pipe to audit_logs).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // TODO: Pipe to audit_logs or monitoring service
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex items-center justify-center min-h-[400px] p-6">
          <div className="glass-card p-8 text-center max-w-md animate-spring">
            <div className="w-12 h-12 rounded-full bg-danger-light/10 dark:bg-danger-dark/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-danger-light dark:text-danger-dark" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="btn-gold"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
